import { FormEvent, useEffect, useState } from 'react'
import { promptTemplatesApi, type PromptTemplate } from '@/lib/api/prompt-templates'
import { Button } from '@/components/ui/button'

export function PromptTemplatesPanel() {
  const [templates, setTemplates] = useState<PromptTemplate[]>([])
  const [name, setName] = useState('')
  const [content, setContent] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)

  const loadTemplates = () => {
    promptTemplatesApi
      .list()
      .then(setTemplates)
      .catch(() => setTemplates([]))
  }

  useEffect(() => {
    loadTemplates()
  }, [])

  const handleCreate = async (event: FormEvent) => {
    event.preventDefault()
    if (!name.trim() || !content.trim()) return

    setBusy(true)
    setMessage(null)
    try {
      await promptTemplatesApi.create(name.trim(), content.trim())
      setName('')
      setContent('')
      loadTemplates()
      setMessage('Template saved.')
    } catch {
      setMessage('Failed to save template.')
    } finally {
      setBusy(false)
    }
  }

  const handleDelete = async (id: string) => {
    setBusy(true)
    setMessage(null)
    try {
      await promptTemplatesApi.delete(id)
      loadTemplates()
      setMessage('Template deleted.')
    } catch {
      setMessage('Failed to delete template.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="rounded-lg border border-border bg-card p-4 md:col-span-2" data-testid="settings-prompt-templates">
      <h2 className="text-sm font-semibold">Prompt Templates</h2>
      <p className="mt-1 text-xs text-muted-foreground">
        Reusable system prompt presets for chat sessions.
      </p>

      <form onSubmit={handleCreate} className="mt-4 grid gap-3 md:grid-cols-2">
        <label className="grid gap-1 text-sm">
          <span className="text-muted-foreground">Name</span>
          <input
            type="text"
            value={name}
            onChange={(event) => setName(event.target.value)}
            className="min-h-11 rounded-md border border-input bg-background px-3 py-2"
            data-testid="prompt-template-name"
          />
        </label>

        <label className="grid gap-1 text-sm md:col-span-2">
          <span className="text-muted-foreground">Content</span>
          <textarea
            value={content}
            onChange={(event) => setContent(event.target.value)}
            rows={4}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
            data-testid="prompt-template-content"
          />
        </label>

        <Button type="submit" size="sm" disabled={busy || !name.trim() || !content.trim()} data-testid="prompt-template-save">
          Save template
        </Button>
      </form>

      {message ? <p className="mt-3 text-sm text-muted-foreground">{message}</p> : null}

      <ul className="mt-4 space-y-2">
        {templates.map((template) => (
          <li
            key={template.id}
            className="rounded-md border border-border px-3 py-2"
            data-testid={`prompt-template-item-${template.id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-medium">{template.name}</p>
                <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{template.content}</p>
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => void handleDelete(template.id)}
                data-testid={`prompt-template-delete-${template.id}`}
              >
                Delete
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}
